import React, { useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import MobileNav from './components/MobileNav';
import LoginModal from './components/Auth/LoginModal';

// Pages
import LandingPage from './components/LandingPage';
import BrowseVacancies from './pages/BrowseVacancies';
import VacancyDetail from './pages/VacancyDetail';
import PostVacancy from './pages/PostVacancy';
import MyReferrals from './pages/MyReferrals';
import Messages from './pages/Messages';
import CompanyDirectory from './pages/CompanyDirectory';
import CompanyProfile from './pages/CompanyProfile';
import ProfileSettings from './components/Profile/ProfileSettings';
import OAuthCallback from './pages/OAuthCallback';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';

function AppContent() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [darkMode, setDarkMode] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginModalMode, setLoginModalMode] = useState<'login' | 'signup'>('login');

  const handleLoginClick = () => {
    setLoginModalMode('login');
    setShowLoginModal(true);
  };

  const handleSignupClick = () => {
    setLoginModalMode('signup');
    setShowLoginModal(true);
  };

  const toggleDarkMode = () => setDarkMode(prev => !prev);

  return (
    <div className={`app ${darkMode ? 'dark' : 'light'}`}>
      <Header
        darkMode={darkMode}
        onDarkModeToggle={toggleDarkMode}
        onMobileMenuToggle={() => setIsMobileNavOpen(true)}
        onLoginClick={handleLoginClick}
        onSignupClick={handleSignupClick}
      />

      <MobileNav
        isOpen={isMobileNavOpen}
        onClose={() => setIsMobileNavOpen(false)}
        darkMode={darkMode}
        onLoginClick={handleLoginClick}
      />

      <main className="main-content">
        <Routes>
          <Route path="/" element={<LandingPage darkMode={darkMode} />} />
          <Route path="/companies" element={<CompanyDirectory darkMode={darkMode} />} />
          <Route path="/companies/:companyId" element={<CompanyProfile darkMode={darkMode} />} />
          <Route path="/vacancies" element={<BrowseVacancies darkMode={darkMode} />} />
          <Route
            path="/vacancies/:id"
            element={<VacancyDetail darkMode={darkMode} onLoginClick={handleLoginClick} />}
          />
          <Route
            path="/post-vacancy"
            element={authLoading ? null : isAuthenticated ? <PostVacancy darkMode={darkMode} /> : <Navigate to="/" />}
          />
          <Route
            path="/my-referrals"
            element={authLoading ? null : isAuthenticated ? <MyReferrals darkMode={darkMode} /> : <Navigate to="/" />}
          />
          <Route
            path="/messages"
            element={authLoading ? null : isAuthenticated ? <Messages darkMode={darkMode} /> : <Navigate to="/" />}
          />
          <Route
            path="/profile"
            element={authLoading ? null : isAuthenticated ? <ProfileSettings darkMode={darkMode} /> : <Navigate to="/" />}
          />
          <Route path="/auth/:provider/callback" element={<OAuthCallback />} />
        </Routes>
      </main>

      <Footer darkMode={darkMode} />

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        darkMode={darkMode}
        actionType="general"
        initialMode={loginModalMode}
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
